import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import { useConfig } from '@app/config';
import { gql, request } from 'graphql-request';
import { useNftMarketplaceFiltersState } from '@app/state/nftMarketplaceFiltersStore';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import { AUCTION_STATUS } from '@app/constants/nftMarketplace';
import { add, subDays } from 'date-fns';
import { useAddress } from '@thirdweb-dev/react';

async function fetcher(
  apiUrl: string | undefined,
  sortOrder: string,
  auctionType: string,
  auctionStatus: string,
  isMyListingsOnly: boolean,
  address: string | undefined,
  searchTerm: string | undefined,
  premiumNftView: string,
  first: number,
  skip: number
) {
  if (!apiUrl || (isMyListingsOnly && !address)) {
    return null;
  }

  const isNFTAvailableParam = auctionStatus !== AUCTION_STATUS.RECENTLY_SOLD;

  const endingSoonThreshold = Math.floor(
    add(new Date(), { days: 1 }).getTime() / 1000
  );
  const endingSoonParam =
    auctionStatus === AUCTION_STATUS.ENDING_SOON
      ? `{ end_lte: ${endingSoonThreshold}, end_gte: ${Math.floor(
          new Date().getTime() / 1000
        )} },`
      : '';

  const recentlySoldThreshold = Math.floor(
    subDays(new Date(), 3).getTime() / 1000
  );
  const recentlySoldParam =
    auctionStatus === AUCTION_STATUS.RECENTLY_SOLD
      ? `{ end_gte: ${recentlySoldThreshold}, end_lte: ${Math.floor(
          new Date().getTime() / 1000
        )}, buyer_not: "0x0000000000000000000000000000000000000000" },`
      : '';

  const makerParam =
    isMyListingsOnly && address ? `{ maker: "${address.toLowerCase()}" },` : '';

  const searchCondition = searchTerm
    ? `{ or: [{token0_: {symbol_contains_nocase: "${searchTerm}"}}, {token1_: {symbol_contains_nocase: "${searchTerm}"}}] },`
    : '';

  const premiumNftViewCondition = premiumNftView
    ? `{ and: [{ nftMetadata_: {isPremium: true} }, { nftContractAddress: "${premiumNftView}" }] }`
    : '';

  const res = await request<{ listings: NFTMarketplaceListing[] }>(
    `${apiUrl}`,
    gql`
      query {
        listings (
          orderBy: price,
           orderDirection: ${sortOrder},
           where: {
              and: [
                { nftPool_not: null },
                { isNFTAvailable: ${isNFTAvailableParam} },
                ${auctionType ? `{ type: "${auctionType}" }` : ''}
                ${endingSoonParam}    
                ${recentlySoldParam}
                ${makerParam}
                ${searchCondition}
                ${premiumNftViewCondition}
              ],  
            },
            first: ${first},
            skip: ${skip}
        ) {
          id
          type
          maker
          currency
          price
          tokenId
          reserve
          nftContractAddress
          amount
          start
          end
          token0 {
            id
            symbol
            name
            decimals
          }
          token1 {
            id
            symbol
            name
            decimals
          }
          isNFTAvailable
          nftPool {
            id
            lpToken {
              symbol
              name
              decimals
              id
            }
          }
          nftMetadata {
            isPremium
            id
          }
        }
      }
    `
  );

  return res.listings.sort((a, b) => {
    // Sort by isPremium: true first
    if (a.nftMetadata.isPremium === b.nftMetadata.isPremium) return 0;

    return a.nftMetadata.isPremium ? -1 : 1;
  });
}

export function useGetNftMarketplaceListings(
  isMyListingsOnly = false,
  first: number,
  skip: number
) {
  const config = useConfig();
  const {
    chain,
    dex,
    sortOrder,
    auctionType,
    auctionStatus,
    searchTerm,
    premiumNftView
  } = useNftMarketplaceFiltersState();
  const address = useAddress();

  // todo - handle chain/dex here
  return useQuery<NFTMarketplaceListing[] | null>(
    [
      QueryKeys.GET_NFT_MARKETPLACE_LISTINGS,
      chain,
      dex,
      sortOrder,
      auctionType,
      auctionStatus,
      address,
      isMyListingsOnly,
      config?.URLS.subgraphUrlNftMarketplace,
      searchTerm,
      premiumNftView,
      first,
      skip
    ],
    () =>
      fetcher(
        config?.URLS.subgraphUrlNftMarketplace,
        sortOrder,
        auctionType,
        auctionStatus,
        isMyListingsOnly,
        address,
        searchTerm,
        premiumNftView,
        first,
        skip
      )
  );
}
